"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";

// Shared frame for real product screenshots on the /platform pages
// (consolidated 2026-08-07 from five identical per-page copies).
// Deliberately SQUARE: these images carry the app's own card corners and
// dark margins inside the pixels, so a rounded wrapper clips through the
// background and produces the mushy double-rounded corner Kevin flagged.
// Click-to-expand lightbox reuses the proven ScreenshotFrame pattern
// (Esc, backdrop click, close button; body scroll locks while open).

const MK_BG = "#0f0f11";
const MK_BD = "rgba(255,255,255,0.06)";
const TEXT = "#EDEDED";

export default function FeatureScreenshot({ src, alt, priority = false }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // aria-label REPLACES the button's accessible name, so pasting the whole alt
  // here produced a 194-character button announcement. Use the first clause as
  // a concise action label; the full description stays on the img itself.
  const first = (alt || "").split(/[,:.]/)[0].trim();
  const label = (first.length > 60 ? first.slice(0, 57).trimEnd() + "..." : first) || "screenshot";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Expand screenshot: ${label}`}
        style={{
          display: "block", width: "100%", padding: 0, margin: 0,
          position: "relative",
          border: `1px solid ${MK_BD}`,
          overflow: "hidden",
          background: MK_BG,
          boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
          cursor: "zoom-in",
        }}
      >
        <Image
          src={src}
          alt={alt}
          width={2940}
          height={1610}
          priority={priority}
          sizes="(max-width: 768px) 100vw, 60vw"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      </button>

      {/* Portaled to <body>: the FadeIn wrappers around every section keep a
          CSS transform, which turns them into containing blocks for
          position: fixed — without the portal the overlay centers inside the
          wrapper instead of the viewport (the "blank/cut off" lightbox bug,
          2026-08-01). Same escape pattern as ScreenshotFrame. */}
      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          onClick={close}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(9,9,11,0.94)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "32px 16px", cursor: "zoom-out",
          }}
        >
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "min(1680px, 96vw)", maxHeight: "88vh",
              width: "auto", height: "auto", cursor: "default",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            }}
          />
          <button
            type="button"
            onClick={close}
            aria-label="Close expanded screenshot"
            style={{
              position: "absolute", top: 16, right: 16,
              width: 44, height: 44, borderRadius: 10,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)",
              color: TEXT, fontSize: 22, lineHeight: 1, cursor: "pointer",
            }}
          >
            &#215;
          </button>
        </div>,
        document.body
      )}
    </>
  );
}
